/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */
/* eslint max-statements: ["error", 40] */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

// TODO Přepracovat logiku Replacer(ů)?
// - Replacer#replace(contentInfo: ContentInfo): ContentInfo
// - SessionReplacer, CommonReplacer (?), ContentTaskReplacer, PowerTaskReplacer
// - Dále vyčlenit Restorer#restore(): ContentInfo
// - Z Downloaderu vyčlenit AssetReplacer?
// - CommonReplacer pravěpodobně rozdělit na jednotlivé datové sklady
// - V souvislosti s úlohami tedy volat postupě TaskReplacer
// - Byl proveden pokus o základní nasazení, ale objevili se složitější
//   problémy, viz. poznámky ohledně slučování u ContentInfo.
/**
 * Nahrazovač obsahu.
 * @implements {EventBus}
 */
class Replacer {
  /**
   * Vytvoří nahrazovač obsahu.
   * @param {AutorebootReplacer} autorebootReplacer
   * @param {ContentTaskReplacer} contentTaskReplacer
   * @param {Downloader} downloader
   * @param {Logger} logger
   * @param {PowerTaskReplacer} powerTaskReplacer
   * @param {Store} primaryStore Hlavní datový sklad ve kterém je uložen přehrávaný obsah.
   * @param {PromiseUtil} promiseUtil
   * @param {QueueFactory} queueFactory Továrna na fronty asynchronních akcí.
   * @param {ReplacerCommon} replacerCommon
   * @param {ReplacerControl} replacerControl
   * @param {ReplacerPower} replacerPower
   * @param {ReplacerScreen} replacerScreen
   * @param {ReplacerSession} replacerSession
   * @param {ReplacerSound} replacerSound
   */
  constructor(
    autorebootReplacer,
    contentTaskReplacer,
    downloader,
    logger,
    powerTaskReplacer,
    primaryStore,
    promiseUtil,
    queueFactory,
    replacerCommon,
    replacerControl,
    replacerPower,
    replacerScreen,
    replacerSession,
    replacerSound
  ) {
    this.autorebootReplacer = autorebootReplacer;
    this.contentTaskReplacer = contentTaskReplacer;
    this.downloader = downloader;

    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.logger = logger;
    this.powerTaskReplacer = powerTaskReplacer;
    this.primaryStore = primaryStore;
    this.promiseUtil = promiseUtil;
    /**
     * Fronta asynchronních změn obsahu.
     * @private
     * @type {Queue}
     */
    this.replaceContentQueue = queueFactory.create();
    this.restoreMediaQueue = queueFactory.create();
    this.replacerCommon = replacerCommon;
    this.replacerControl = replacerControl;
    this.replacerPower = replacerPower;
    this.replacerScreen = replacerScreen;
    this.replacerSession = replacerSession;
    this.replacerSound = replacerSound;
    // Navázání naslouchání na instanci.
    this.onContent = this.onContent.bind(this);
    // Naslouchání událostem.
    downloader.on('content', this.onContent);
  }

  /**
   * Aplikuje obsah na zařízení.
   * V této akci se synchronizuje nastavení zařízení s obsahem.
   * Jde o čistě stavové operace, není zde žádný výsledek,
   * pouze se nastaví to, co je požadováno v obsahu.
   * @param {ContentInfo} contentInfo
   * @private
   * @returns {undefined}
   */
  applyContent(contentInfo) {
    return new Promise((resolve) =>
      resolve(
        // Seznam akcí, které se mají provést.
        [
          () => this.autorebootReplacer.replace(contentInfo),
          () => this.replacerControl.replace(contentInfo),
          () => this.replacerSound.replace(contentInfo),
          () => this.replacerPower.replace(contentInfo),
          () => this.replacerScreen.replace(contentInfo)
        ]
          // Sériové volání všech akcí.
          .reduce(
            (acc, callback) =>
              acc.then(() =>
                callback()
                  // Selhání jedné akce, nepřeruší spuštění ostatních.
                  .catch((error) => this.logger.error(error))
              ),
            Promise.resolve()
          )
      )
    );
  }

  /**
   * Uvolní všechny zdroje pro garbage collection.
   * @returns {undefined} Nic.
   */
  destroy() {
    // Zrušení stahovače souborů.
    this.downloader.off('content', this.onContent);
    this.downloader.destroy();
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na požadavek na přepnutí obsahu ze stahovače.
   * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @private
   * @returns {undefined} Nic.
   */
  onContent(contentInfo) {
    this.emitter.emit('content', contentInfo);
  }

  /**
   * Nahradí stávající obsah za nový.
   * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @returns {Promise.<ContentInfo>} Nahrazený obsah.
   */
  replace(contentInfo) {
    // TODO #84 Propracovat lépe nahrazení obsahu s přerušením stahování.
    // - Využívat připravené metody Downloader.cancel()
    // - Problematický případ:
    //   - Stahuje se veliký soubor, který nelze rychle přerušit.
    //   - Přijde nový obsah, který vyžádá přerušení a čeká než je dokončeno.
    //   - Přijde další nový obsah, který by měl zrušit čekající a zařadit se v čekání na jeho místo.
    //   - Atd. dokud není přerušení stahování dokončeno. Pak se spustí stahování čekajícího obsahu.
    // - Analogický problematický případ nastavá u náhrady datového skladu, který nejde přerušit.
    //   - Zde je šance na vyvolání mnohem nižší (je rychle provedený), ale stále teoreticky možná.
    // - Implementace:
    //   - Příznak replacing, podle kterého se rozhodne, jestli při spuštění rušit download.
    //   - Čekající obsah s možností jeho okamžitého ukončení.
    // - Unit testy.
    return new Promise((resolve) =>
      resolve(
        // TODO Neměla by být fronta spíše v nadřazené třídě (Device)?
        this.replaceContentQueue.enqueue(() =>
          this.replaceContent(contentInfo).then((replacedContentInfo) =>
            this.applyContent(replacedContentInfo).then(
              () => replacedContentInfo
            )
          )
        )
      )
    );
  }

  /**
   * Nahradí stávající obsah za nový.
   * Obsahuje akce stahující obsah a nahrazení datového skladu.
   * @param {ContentInfo} contentInfo
   * @returns {Promise.<ContentInfo>}
   */
  replaceContent(contentInfo) {
    return new Promise((resolve) =>
      resolve(
        this.promiseUtil
          .allSerial([
            // Aktualizace požadovaného typu jednotky do session ještě před zahájením stahování.
            () => this.replaceStorage(contentInfo),
            // Stažení binárních souborů.
            () => this.downloadMedia(contentInfo),
            // Aktualizace datových zdrojů.
            () => this.replacerSession.replace(contentInfo),
            () => this.replacerCommon.replace(contentInfo),
            () => this.contentTaskReplacer.replace(contentInfo),
            () => this.powerTaskReplacer.replace(contentInfo)
          ])
          .then(
            ([storage, assets, session, commons, contentTasks, powerTasks]) => {
              const { primaryStore } = this;

              return {
                contentTasks,
                firmwareRelease: contentInfo.firmwareRelease,
                layerArray: commons.layer,
                layoutArray: commons.layout,
                mediumArray: commons.medium,
                playlistArray: commons.playlist,
                powerTasks,
                session,
                store: primaryStore,
                taskArray: commons.task,
                timerArray: commons.timer,
                trackArray: commons.track
              };
            }
          )
      )
    );
  }

  /**
   * Obnoví stávající obsah.
   * @returns {Promise.<ContentInfo>} Vyřeší se po obnovení obsahu.
   */
  restore() {
    return new Promise((resolve) =>
      resolve(
        this.restoreContent().then((contentInfo) =>
          this.applyContent(contentInfo).then(() => contentInfo)
        )
      )
    );
  }

  replaceStorage(contentInfo) {
    return new Promise((resolve) => {
      const { session } = contentInfo;

      // Prázdný řetězec je také povolený vstup
      if (isNil(session.storage)) {
        resolve();
        return;
      }
      return resolve(
        this.primaryStore.update('session', session.id, {
          storage: session.storage
        })
      );
    });
  }

  downloadMedia(contentInfo) {
    return new Promise((resolve) => {
      resolve(this.restoreMedia(contentInfo));
    });
  }

  restoreMedia(contentInfo) {
    return new Promise((resolve) => {
      resolve(
        this.restoreMediaQueue.enqueue(() =>
          this.downloader.download(contentInfo)
        )
      );
    });
  }

  /**
   * Obnoví obsah z perzistentího úložiště a případně ho pročistí.
   * @returns {Promise.<ContentInfo>}
   */
  restoreContent() {
    return new Promise((resolve) =>
      resolve(
        // Obnoví se obsah z LocalStorage včetně starých Task.
        this.promiseUtil
          .allSerial([
            // Obnovení stažených binárních souborů.
            () => this.downloader.start(),
            // Obnoví datové zdroje.
            () => this.replacerSession.restore(),
            () => this.replacerCommon.restore()
          ])
          .then(([assets, session, commons]) => {
            const { primaryStore } = this;
            const taskArray = commons.task;
            return (
              // S využitím starých Task se obnoví i ContentTask a PowerTask.
              this.promiseUtil
                .allSerial([
                  () => this.contentTaskReplacer.restore(taskArray),
                  () => this.powerTaskReplacer.restore(taskArray)
                ])
                // Z obnovených hodnot se vytvoří nový obsah.
                .then(([contentTasks, powerTasks]) => ({
                  contentTasks,
                  firmwareRelease: null,
                  layerArray: commons.layer,
                  layoutArray: commons.layout,
                  mediumArray: commons.medium,
                  playlistArray: commons.playlist,
                  powerTasks,
                  session,
                  store: primaryStore,
                  taskArray,
                  timerArray: commons.timer,
                  trackArray: commons.track
                }))
            );
          })
      )
    );
  }
}

/**
 * Nahrazený obsah zařízení k nastavení.
 * V rámci jednoho nahrazení může být vyvoláno více událostí.
 * @event Replacer#content
 * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
 */

export default Replacer;
